import { boolean, index, jsonb, pgTable, text, timestamp, uuid } from "drizzle-orm/pg-core";

/**
 * The platform audit log: one row per action taken with authority, on every request path.
 *
 * Writer: the audit middleware of each transport and nothing else — the dashboard's tRPC
 * ladder (`packages/functions/src/dashboard-api/trpc/trpc.ts`), Better Auth's after hook,
 * the public API's `/v1` middleware and the Fulfillment API's `route()` wrapper. Rows are
 * append-only: migration 0033 installs a trigger that refuses UPDATE, DELETE and TRUNCATE, so
 * no procedure, route or job can change one. Read by platform admins only
 * (`platform.auditLogs.list`); nothing here reaches a tenant-facing response.
 *
 * `tenant_id` and `environment_id` carry no foreign key on purpose: platform actions have
 * neither, and a deleted tenant's rows are what remains of it.
 */
export const auditLogs = pgTable(
  "audit_logs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /** admin (a dashboard session) | api_key | system | anonymous. */
    actorType: text("actor_type").notNull(),
    /**
     * A Better Auth user id, an API key id, or a system name (`storefront-bff`,
     * `bot-job-api`). No FK: system actors have no user row.
     */
    actorId: text("actor_id").notNull(),
    /**
     * What was done: a tRPC path (`users.setLock`), a public API route
     * (`POST /v1/users/:externalId/balance/credits`), a Fulfillment API route key
     * (`POST /orders`) or an auth endpoint (`auth.sign-in.email`).
     */
    action: text("action").notNull(),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id"),
    /** The tenant the action was scoped to, when it had one. Null for platform actions. */
    tenantId: text("tenant_id"),
    /**
     * The environment the action was scoped to, when it named one. Null for a tenant procedure
     * means "the live environment at the time"; null for a platform action means none.
     */
    environmentId: text("environment_id"),
    success: boolean("success").notNull().default(true),
    /** `{ input, output?, details }`, every key allowlisted by the transport's registry. */
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("audit_logs_actor_created_idx").on(t.actorType, t.actorId, t.createdAt),
    index("audit_logs_resource_created_idx").on(t.resourceType, t.resourceId, t.createdAt),
    index("audit_logs_created_idx").on(t.createdAt),
    index("audit_logs_tenant_created_idx").on(t.tenantId, t.createdAt),
  ],
);
